import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { hashPackService } from '../services/wallets/hashpack/hashPackService';

interface HashPackWalletContextType {
  isConnected: boolean;
  accountId: string | null;
  accounts: Array<{ accountId: string; accountBalance: string }>;
  loading: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const HashPackWalletContext = createContext<HashPackWalletContextType | null>(null);

interface HashPackWalletProviderProps {
  children: ReactNode;
}

export const HashPackWalletProvider: React.FC<HashPackWalletProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Array<{ accountId: string; accountBalance: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to wallet state changes
    const unsubscribe = hashPackService.onStateChange((state) => {
      console.log('HashPack wallet state changed:', state);
      setIsConnected(state.isConnected);
      setAccounts(state.accounts);
      setAccountId(state.selectedAccount?.accountId || null);

      // Store connection state in localStorage for persistence
      if (state.isConnected && state.selectedAccount) {
        localStorage.setItem('hashpack_connected', 'true');
        localStorage.setItem('hashpack_accountId', state.selectedAccount.accountId);
      } else {
        localStorage.removeItem('hashpack_connected');
        localStorage.removeItem('hashpack_accountId');
      }
    });

    // Check for persisted connection on mount
    const checkPersistedConnection = () => {
      const wasConnected = localStorage.getItem('hashpack_connected') === 'true';
      const persistedAccountId = localStorage.getItem('hashpack_accountId');

      if (wasConnected && persistedAccountId) {
        // Check if HashPack is still connected
        const currentAccountId = hashPackService.getPrimaryAccountId();
        if (currentAccountId === persistedAccountId) {
          setIsConnected(true);
          setAccountId(currentAccountId);
          console.log('✅ Restored HashPack connection:', currentAccountId);
        } else {
          // Clear stale connection data
          localStorage.removeItem('hashpack_connected');
          localStorage.removeItem('hashpack_accountId');
        }
      }
    };

    // Check after a short delay to allow HashConnect to initialize
    setTimeout(checkPersistedConnection, 1000);

    return unsubscribe;
  }, []);

  const connect = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔗 Connecting to HashPack...');

      const accounts = await hashPackService.connectWallet();
      if (accounts.length > 0) {
        console.log('✅ HashPack connection successful');
        // The state change will be handled by the onStateChange listener
      } else {
        throw new Error('No accounts returned from HashPack');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to HashPack';
      console.error('❌ HashPack connection failed:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔌 Disconnecting from HashPack...');

      await hashPackService.disconnectWallet();
      setIsConnected(false);
      setAccountId(null);
      setAccounts([]);
      console.log('✅ HashPack disconnected successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to disconnect from HashPack';
      console.error('❌ HashPack disconnection failed:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const value: HashPackWalletContextType = {
    isConnected,
    accountId,
    accounts,
    loading,
    error,
    connect,
    disconnect,
  };

  return (
    <HashPackWalletContext.Provider value={value}>
      {children}
    </HashPackWalletContext.Provider>
  );
};

export const useHashPackWallet = (): HashPackWalletContextType => {
  const context = useContext(HashPackWalletContext);
  if (!context) {
    throw new Error('useHashPackWallet must be used within a HashPackWalletProvider');
  }
  return context;
};